// Loads the current user's identity + permissions from GET /me once, and exposes
// a `can()` helper so the UI shows only what the backend will allow. The matrix
// is served by the backend, so UI gating and server enforcement share one source.
//
// can(permission, { eventId }) — true when a global role/capability grants it,
// or when the user's *active* attendance role on `eventId` grants it.
// can(permission, { anyEvent: true }) — as above but satisfied by an active role on
// *any* of their events, for pages whose content isn't tied to one event.
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "./config";
import { useViewAs } from "./ViewAsContext";

const MeContext = createContext({ me: null, loading: true });

export function MeProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  // Re-fetch whenever a "View as" session starts/stops/toggles writes, so the
  // whole UI re-gates as the impersonated person (the axios interceptor has
  // already picked up the new headers from localStorage by the time we fetch).
  const { token } = useViewAs();

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API_BASE_URL}/me/`)
      .then(res => { if (active) setMe(res.data); })
      .catch(() => { if (active) setMe(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const value = useMemo(() => ({ me, loading }), [me, loading]);
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe() {
  const { me, loading } = useContext(MeContext);

  const can = (permission, { eventId, anyEvent } = {}) => {
    if (!me) return false;
    // capabilities is the effective global-context set (covers super, global
    // roles, self pages and the admin toggle).
    if (me.capabilities?.[permission]) return true;
    if (anyEvent && me.any_event_capabilities?.[permission]) return true;
    if (eventId != null) {
      const er = me.event_roles?.[eventId] || me.event_roles?.[String(eventId)];
      const rule = me.matrix?.permissions?.[permission];
      if (er?.active && rule && rule.event.includes(er.role)) return true;
    }
    return false;
  };

  return { me, loading, can };
}
